import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { GameType, GolfEvent, GolfTournament, HoleResult, MatchFilter, RyderCupTeamKeyType } from 'src/app/_interfaces/golf/golf-tournament';

import { GolfPlayer } from 'src/app/_interfaces';
import { GolfScoreCardModel } from 'src/app/_interfaces/golf/golf-score-card';
import { AppStringsService } from 'src/app/_services';
import { RYDER_CUP_TEAMS, RyderCupTeamKey } from 'src/app/_constants/ryder-cup-team.constants';
import { GolfEventStatus, TeamScore } from 'src/app/_interfaces/golf/gol-match-card';
import { MatchPlayScorecardComponent } from '../matchplay-scorecard/matchplay-scorecard.component';

import { formatLocalShortTimeFromCET } from 'src/app/_functions/datetimes';
import { ImageComponent } from '../../image/image.component';
import { SponsorBannerComponent } from '../../sponsor-banner/sponsor-banner.component';

type MatchVM = {
  id: string;
  label: '1 v 1' | 'Foursomes';
  left: GolfScoreCardModel[];
  right: GolfScoreCardModel[];
  status: GolfEventStatus
  statusLabel: string;  // NOTE: this will be "Thru X" when LIVE & thru exists, else localized status
  scoreText: string;
  scoreBg?: string | null;
  scoreTeamKey?: 'eu' | 'us' | 'team1' | 'team2';
  aggregate?: Array<HoleResult>;
  extraAggregate?: Array<HoleResult>;
  gameType?: GameType
  event?: GolfEvent;
};

@Component({
    selector: 'app-golf-match-cards',
    imports: [CommonModule, ImageComponent, MatchPlayScorecardComponent, SponsorBannerComponent],
    templateUrl: './golf-match-cards.component.html',
    styleUrls: ['./golf-match-cards.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class GolfMatchCardsComponent {
  private _golfTour!: GolfTournament;

  @Input({ required: true }) set golfTour(val: GolfTournament) {
      this._golfTour = val;
      this.rebuild();
  }

  get golfTour(): GolfTournament { 
      return this._golfTour; 
  }

  private _filter: MatchFilter | null = null;

  @Input() set filter(val: MatchFilter | null) {
      const next = val ? { ...val } : null;
      const changed = JSON.stringify(next) !== JSON.stringify(this._filter);
      this._filter = next;
      if (changed) this.rebuild();
  }

  get filter(): MatchFilter | null { 
      return this._filter; 
  }

  vm: MatchVM[] = [];

  expandedIds = new Set<string>();

  constructor(
    private appStrings: AppStringsService
  ) {}

  private rebuild(): void {
      if (!this._golfTour) {
          this.vm = [];
          return;
      }

      const evtsAll = this._golfTour?.events ?? [];
      const evts = evtsAll
          .filter(e => e?.type === 'match_play' && !e?.is_primary_team_event)
          .filter(e => {
              if (!this._filter) return true;
              const dateKey = this.toLocalDateKey(e?.start_date);
              const gt = e?.game_type as string | null | undefined;
              return (
                  dateKey === this._filter.dateKey &&
          ((gt === 'single'     && this._filter.gameType === 'single') ||
           (gt === 'four_ball'  && this._filter.gameType === 'four_ball') ||
           (gt === 'four_some'  && this._filter.gameType === 'four_some'))
              );
          });

      const ryderMode = this.isRyderCupLike();

      this.vm = evts
          .filter(e => Array.isArray(e?.scores) && e.scores.length === 2)
          .map((e): MatchVM => {
              const left = (((e.scores as Array<GolfScoreCardModel[]>)?.[0]) ?? []).filter((p) => p?.player);
              const right = (((e.scores as Array<GolfScoreCardModel[]>)?.[1]) ?? []).filter((p) => p?.player);
              const label: MatchVM['label'] = (left.length === 1 && right.length === 1) ? '1 v 1' : 'Foursomes';
              const status = this.normalizeStatus(e?.status);
              const thru = this.extractThru(left, right);
              const statusLabel = this.computeDisplayStatusLabel(status, thru);
              const { scoreText, scoreBg, scoreTeamKey } = this.extractScore(e?.team_score, ryderMode);
              const aggregate = e?.aggregate_mp_scores as MatchVM['aggregate'];
              const extraAggregate = e?.extra_aggregate_mp_scores as MatchVM['extraAggregate'];
              const gameType = (e?.game_type as MatchVM['gameType']);
              return { id: e.id!, label, left, right, status, statusLabel, scoreText, scoreBg, scoreTeamKey, aggregate, extraAggregate, gameType, event: e };
          });
  }

  toggleExpand(m: MatchVM): void {
      if (this.expandedIds.has(m.id)) this.expandedIds.delete(m.id);
      else this.expandedIds.add(m.id);
  }

  private toLocalDateKey(iso?: string | null): string | null {
      if (!iso) return null;
      const d = new Date(iso);
      const year = d.getFullYear();
      const month = `${d.getMonth() + 1}`.padStart(2, '0');
      const day = `${d.getDate()}`.padStart(2, '0');
      return `${year}-${month}-${day}`;
  }

  private extractScore(
      teamScore: TeamScore | undefined,
      ryderMode: boolean,
  ): { scoreText: string; scoreBg?: string | null; scoreTeamKey?: 'eu'|'us'|'team1'|'team2' } {
      if (!teamScore) return { scoreText: '' };

      const t1 = teamScore.team_1;
      const t2 = teamScore.team_2;

      const scoreText = (t1?.mp_score ?? t2?.mp_score ?? '') as string;

      if (ryderMode) {
          if (t1?.mp_result === 'won') {
              return { scoreText, scoreBg: this.colorForTeamKey(this.toTeamName(t1?.name)), scoreTeamKey: this.toDataTeamKey(t1?.name) };
          }
          if (t2?.mp_result === 'won') {
              return { scoreText, scoreBg: this.colorForTeamKey(this.toTeamName(t2?.name)), scoreTeamKey: this.toDataTeamKey(t2?.name) };
          }

          const s1 = (t1?.mp_score ?? '').toUpperCase();
          const s2 = (t2?.mp_score ?? '').toUpperCase();
          const isTie = (s1 === 'T' && s2 === 'T');

          if (!isTie) {
              if (s1 && s1 !== 'T') {
                  return { scoreText: scoreText || s1, scoreBg: this.colorForTeamKey(this.toTeamName(t1?.name)), scoreTeamKey: this.toDataTeamKey(t1?.name) };
              }
              if (s2 && s2 !== 'T') {
                  return { scoreText: scoreText || s2, scoreBg: this.colorForTeamKey(this.toTeamName(t2?.name)), scoreTeamKey: this.toDataTeamKey(t2?.name) };
              }
          }
          return { scoreText };
      }

      const RED  = 'var(--mp-team1-red, #ef4444)';
      const BLUE = 'var(--mp-team2-blue, #3b82f6)';

      if (t1?.mp_result === 'won') {
          return { scoreText, scoreBg: RED, scoreTeamKey: 'team1' }
      }
      if (t2?.mp_result === 'won') {
          return { scoreText, scoreBg: BLUE, scoreTeamKey: 'team2' };
      }

      const s1 = (t1?.mp_score ?? '').toUpperCase();
      const s2 = (t2?.mp_score ?? '').toUpperCase();
      const isTie = (s1 === 'T' && s2 === 'T');

      if (!isTie) {
          if (s1 && s1 !== 'T') {
              return { scoreText: scoreText || s1, scoreBg: RED,  scoreTeamKey: 'team1' };
          }
          if (s2 && s2 !== 'T') {
              return { scoreText: scoreText || s2, scoreBg: BLUE, scoreTeamKey: 'team2' }; 
          }
      }

      return { scoreText };
  }

  private toDataTeamKey(name?: string | null): 'eu' | 'us' | undefined { 
      const k = (name || '').toLowerCase();
      if (k === 'europe') return 'eu';
      if (k === 'usa') return 'us';
      return undefined;
  }

  private toTeamName(name?: string | null): RyderCupTeamKeyType | undefined {
      const k = (name || '').toLowerCase();
      return k === 'usa' || k === 'europe' ? (k as RyderCupTeamKeyType) : undefined;
  }

  private colorForTeamKey(key?: keyof typeof RyderCupTeamKey | string): string | null {
      const k = (String(key || '').toLowerCase()) as RyderCupTeamKeyType;
      if (k === 'usa')    return RYDER_CUP_TEAMS[RyderCupTeamKey.USA].color;
      if (k === 'europe') return RYDER_CUP_TEAMS[RyderCupTeamKey.EUROPE].color;
      return null;
  }

  private normalizeStatus(raw?: string): MatchVM['status'] {
      const s = (raw || '').toLowerCase().replace(/[\s_-]+/g, '');
      if (s === 'finished') return 'finished';
      if (s === 'inprogress' || s === 'live' || s === 'ongoing') return 'inprogress';
      if (s === 'interrupted' || s === 'suspended' || s === 'weatherdelay') return 'interrupted';
      return 'unknown';
  }

  private localizeStatus(s: MatchVM['status']): string {
      const key = s === 'finished' ? 'golf.eventstatus.finished.match' :
          s === 'inprogress' ? 'golf.eventstatus.inprogress.match' :
              s === 'interrupted' ? 'golf.eventstatus.interrupted.match' : '';

      return key ? (this.appStrings.getAppString?.(key) ?? this.appStrings.cache.get(key) ?? key) : '';
  }

  trackByMatch = (_: number, m: MatchVM) => m.id;

  shortName(p: GolfPlayer): string {
      const f = (p.first_name || '').trim();
      const l = (p.last_name || '').trim();
      const initial = f ? `${f.charAt(0)}.` : '';
      return `${initial} ${l}`.trim();
  }

  flagCode(p: GolfPlayer): string {
      const team = (p.ryder_cup_team || '').toLowerCase();
      if (team === 'europe') return 'eu';
      if (team === 'usa') return 'us';
      return p.country?.toLocaleLowerCase() || 'un';
  }

  hasScorecard(m: MatchVM): boolean {
      const hasPlayers = (Array.isArray(m.left) && m.left.length > 0) || (Array.isArray(m.right) && m.right.length > 0);
      const hasAggregate = Array.isArray(m.aggregate) && m.aggregate.length > 0;
      return !!(hasPlayers || hasAggregate);
  }

  toggleExpandFromIcon(ev: Event, m: MatchVM): void {
      ev.stopPropagation();
      this.toggleExpand(m);
  }

  shouldShowTeeTime(m: MatchVM): boolean {
      const st = (m.event?.status || '').toLowerCase().replace(/[\s_]/g, '');
      const notStarted = st === 'notstarted';
      const isMatch = m.event?.is_primary_team_event === false;
      return Boolean(notStarted && isMatch && m.event?.start_date);
  }

  teeTimeLabel(m: MatchVM): string {
      return formatLocalShortTimeFromCET(m.event?.start_date ?? '');
  }

  /**
   * Decide which label to show under the score:
   * - finished  -> localized FINAL
   * - interrupted -> localized INTERRUPTED
   * - inprogress and thru present -> "Thru X" (localized)
   * - inprogress and no thru -> localized LIVE
   */
  private computeDisplayStatusLabel(status: MatchVM['status'], thru: number | null): string {
      if (status === 'finished')   return this.localizeStatus(status);
      if (status === 'interrupted') return this.localizeStatus(status);
      if (status === 'inprogress') {
          if (thru && thru > 0) return this.formatThruLabel(thru);
          return this.localizeStatus(status);
      }
      return this.localizeStatus(status);
  }

  private formatThruLabel(n: number): string {
      const key = 'golf.leaderboard.thru.matchplay';
      const t = this.appStrings.getAppString?.(key) ?? this.appStrings.cache.get(key);
      if (typeof t === 'string') {
          if (t.includes('%@')) return t.replace('%@', String(n));
          if (t.includes('{0}')) return t.replace('{0}', String(n));
      }
      return `Thru ${n}`;
  }

  /**
   * Extract the "thru" hole number for LIVE matches.
   * Source: GolfScoreCardModel.round_final?.thru (player 1 & player 2).
   */
  private extractThru(
      left: GolfScoreCardModel[] | undefined,
      right: GolfScoreCardModel[] | undefined,
  ): number | null {
      const thruFromFirst = (side?: GolfScoreCardModel[] | null): number | null => {
          const t = side?.[0]?.round_final?.thru;
          return (typeof t === 'number' && t > 0) ? Math.min(18, t) : null;
      };

      const vals: number[] = [];
      const l = thruFromFirst(left);  if (l != null) vals.push(l);
      const r = thruFromFirst(right); if (r != null) vals.push(r);

      if (!vals.length) return null;
      return Math.max(...vals);
  }

  // Decide if a banner should render after index i
  // Show after every 6th card (i+1) % 6 === 0, but NEVER if that position would be the last item.
  shouldShowBanner(i: number): boolean {
      const afterThis = i + 1;
      return afterThis % 6 === 0 && i < (this.vm.length - 1);
  }

  private isRyderCupLike(): boolean {
      const evts = this._golfTour?.events ?? [];
      return evts.some(e => {
          const n1 = e?.team_score?.team_1?.name?.toLowerCase?.();
          const n2 = e?.team_score?.team_2?.name?.toLowerCase?.();
          return n1 === 'europe' || n1 === 'usa' || n2 === 'europe' || n2 === 'usa';
      });
  }

  hasPlayoff(m: MatchVM): boolean {
      const arr = m?.extraAggregate;
      return Array.isArray(arr) && arr.some(h => h && h.score != null);
  }

  get playoffText(): string {
      const key = 'golf.scorecard.playoff';
      return this.appStrings.getAppString?.(key) ?? this.appStrings.cache.get(key) ?? 'Playoff';
  }
}

